/**
 * Web Component: collection-promo-banner
 * Banner promocional que se inyecta en el grid de productos Klevu.
 * Atributos: image, image-mobile, video, video-mobile, poster, url, banner-ref
 * Origen de datos: metaobjeto de Shopify o Bloomreach Engagement (window.KCPromoBanners)
 */
(function () {
  const tag = 'collection-promo-banner';

  if (customElements.get(tag)) return;

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Una vista por banner por pageview: la reinyeccion del grid (filtros,
  // paginacion, resize) no debe inflar la metrica.
  const trackedViews = {};

  function trackEngagement(event, payload) {
    try {
      if (typeof window.exponea !== 'undefined' && typeof window.exponea.track === 'function') {
        window.exponea.track(event, payload);
      }
    } catch (e) {}
  }

  class CollectionPromoBanner extends HTMLElement {
    static get observedAttributes() {
      return ['image', 'image-mobile', 'video', 'video-mobile', 'poster', 'url', 'banner-ref'];
    }

    constructor() {
      super();
      this._image = '';
      this._imageMobile = '';
      this._video = '';
      this._videoMobile = '';
      this._poster = '';
      this._url = '#';
      this._bannerRef = 'banner';
      this._observer = null;
      this._mediaCount = 0;
      this._failCount = 0;
    }

    connectedCallback() {
      this._render();
      this._attachClickHandler();
    }

    disconnectedCallback() {
      this._disconnectObserver();
    }

    attributeChangedCallback(name, _old, value) {
      const val = value || '';
      if (name === 'image') this._image = val;
      if (name === 'image-mobile') this._imageMobile = val;
      if (name === 'video') this._video = val;
      if (name === 'video-mobile') this._videoMobile = val;
      if (name === 'poster') this._poster = val;
      if (name === 'url') this._url = value || '#';
      if (name === 'banner-ref') this._bannerRef = value || 'banner';
      if (this.isConnected) {
        this._render();
        this._attachClickHandler();
      }
    }

    _disconnectObserver() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    _hideCell() {
      const li = this.closest('li.klevuBanner');
      if (li) li.style.display = 'none';
    }

    // Solo se oculta la celda si TODAS las piezas fallaron: con desktop+mobile
    // el navegador igual descarga la pieza oculta y un 404 no debe matar el banner.
    _onMediaError() {
      this._failCount++;
      if (this._failCount >= this._mediaCount) this._hideCell();
    }

    _createImg(src, className) {
      const img = document.createElement('img');
      img.src = src || '';
      img.alt = '';
      // 'eager', no 'lazy': el banner se inserta dinamicamente despues del
      // render inicial del grid, y el lazy-load nativo puede no detectar
      // que ya esta en pantalla en ese caso (mas inconsistente en WebKit).
      img.loading = 'eager';
      img.className = className;
      img.onerror = () => this._onMediaError();
      return img;
    }

    _createVideo(src, className) {
      const video = document.createElement('video');
      video.className = className;
      video.loop = true;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.controls = false;
      video.disablePictureInPicture = true;
      // 'auto' (no 'metadata'): en mobile con datos moviles o ahorro de
      // bateria, el autoplay puede demorar o bloquearse; con 'metadata' el
      // navegador ni siquiera baja el primer frame mientras tanto, dejando
      // la celda en gris. Con 'auto' al menos hay un frame visible de inmediato.
      video.preload = 'auto';
      // Los atributos son obligatorios ademas de las propiedades: iOS/Safari
      // solo respetan el autoplay silenciado si vienen en el markup.
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      if (this._poster) video.poster = this._poster;
      video.onerror = () => this._onMediaError();
      video.src = src;

      if (!reduceMotion) {
        video.autoplay = true;
        video.setAttribute('autoplay', '');
      }
      return video;
    }

    _createMedia(videoSrc, imageSrc, videoClass, imageClass) {
      if (videoSrc) {
        this._mediaCount++;
        return this._createVideo(videoSrc, videoClass);
      }
      if (imageSrc) {
        this._mediaCount++;
        return this._createImg(imageSrc, imageClass);
      }
      return null;
    }

    _render() {
      this._disconnectObserver();
      this._mediaCount = 0;
      this._failCount = 0;

      const hasDesktop = this._video || this._image;
      const hasMobile = this._videoMobile || this._imageMobile;

      if (!hasDesktop && !hasMobile) {
        this.innerHTML = '';
        this._hideCell();
        return;
      }

      const wrap = document.createElement('div');
      wrap.className = 'kuProdWrap kuBannerWrap';

      const imgWrap = document.createElement('div');
      imgWrap.className = 'kuBannerImgWrap';

      // Sin URL real, el banner no debe ser clickeable. El default del
      // template parameter en Bloomreach es '#' (no vacio) si el marketer
      // no carga link, asi que ambos casos cuentan como "sin link".
      const hasLink = !!this._url && this._url !== '#';
      const a = document.createElement(hasLink ? 'a' : 'div');
      if (hasLink) {
        a.href = this._url;
        a.target = '_self';
        a.className = 'klevuBannerClick';
      } else {
        a.className = 'klevuBannerStatic';
      }
      a.setAttribute('data-name', this._bannerRef);
      a.setAttribute('aria-label', 'Banner promocional');

      // Una sola pieza en el DOM, elegida aqui en JS segun el viewport actual
      // en vez de crear las dos y dejar que el CSS oculte una: en WebKit real
      // (iOS) confirmamos que el display:none de la media query no siempre
      // se aplica a tiempo sobre un elemento recien insertado por JS, y las
      // dos piezas quedan apiladas visualmente.
      const isMobileViewport =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 768px)').matches;

      let only;
      if (isMobileViewport && hasMobile) {
        only = this._createMedia(this._videoMobile, this._imageMobile, '', '');
      } else if (hasDesktop) {
        only = this._createMedia(this._video, this._image, '', '');
      } else {
        only = this._createMedia(this._videoMobile, this._imageMobile, '', '');
      }
      if (only) a.appendChild(only);

      imgWrap.appendChild(a);
      wrap.appendChild(imgWrap);

      this.innerHTML = '';
      this.appendChild(wrap);

      this._observeVisibility();
    }

    // Un solo observer para las dos cosas que dependen del viewport:
    // reproducir/pausar el video y contar la vista.
    _observeVisibility() {
      if (typeof IntersectionObserver !== 'function') {
        this._playVideos();
        this._trackView();
        return;
      }

      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._playVideos();
              this._trackView();
            } else {
              this._pauseVideos();
            }
          });
        },
        { threshold: 0.25 }
      );

      this._observer.observe(this);
    }

    _playVideos() {
      if (reduceMotion) return;
      const videos = this.querySelectorAll('video');
      for (let i = 0; i < videos.length; i++) {
        const promise = videos[i].play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      }
    }

    _pauseVideos() {
      const videos = this.querySelectorAll('video');
      for (let i = 0; i < videos.length; i++) videos[i].pause();
    }

    _trackView() {
      const ref = this._bannerRef || 'banner';
      if (trackedViews[ref]) return;
      trackedViews[ref] = true;
      trackEngagement('plp_banner_view', {
        banner_ref: ref,
        banner_url: this._url,
        has_video: !!(this._video || this._videoMobile),
        location: window.location.pathname,
      });
    }

    _attachClickHandler() {
      const link = this.querySelector('.klevuBannerClick');
      if (!link || link.dataset.attached === 'true') return;

      link.dataset.attached = 'true';
      link.addEventListener('click', () => {
        const ref = link.getAttribute('data-name') || 'banner';

        // Klevu analytics: se mantiene mientras el PLP siga corriendo sobre Klevu.
        const trackingUrl =
          'https://stats.ksearchnet.com/analytics/datatracking?type=banner&klevu_bannerid=' +
          encodeURIComponent(ref) +
          '&klevu_term=*&klevu_request=click';
        new Image().src = trackingUrl;

        trackEngagement('plp_banner_click', {
          banner_ref: ref,
          banner_url: link.href,
          location: window.location.pathname,
        });
      });
    }
  }

  customElements.define(tag, CollectionPromoBanner);
})();
